import React, { useState, useEffect, useRef } from 'react';
import { FaChevronDown, FaChevronRight, FaFutbol, FaBasketballBall, FaVolleyballBall, FaMapMarkerAlt, FaCheckCircle, FaCcVisa, FaCcMastercard, FaCreditCard, FaArrowLeft } from 'react-icons/fa';
import { GiTennisRacket } from 'react-icons/gi';
import '../../styles/pages/sportsAppointment.scss';
import { getSportsRegionsWithFacilities, getSportsAvailableSlots, createSportsAppointment, verifySportsAppointment } from '../../api/sportsAppointmentService';
import { initiatePayment } from '../../api/paymentService';
import TermsOfService from '../../components/Contracts/TermsOfService';
import RefundPolicy from '../../components/Contracts/RefundPolicy';
import DistanceSalesContract from '../../components/Contracts/DistanceSalesContract';

const facilityIcons = {
  Tenis: GiTennisRacket,
  Futbol: FaFutbol,
  Basketbol: FaBasketballBall,
  Voleybol: FaVolleyballBall,
  'Basketbol/Voleybol': FaBasketballBall,
};

const facilityCoordinates = {
  'Rauf Denktas Tenis Kortlari': '36.587956, 36.146827',
  'Toki Millet Parki Tesisleri': '36.587563, 36.163551',
  'Millet Bahçesi Spor Tesisleri': '36.585065, 36.155411',
  'Mustafa Kemal Kapali Spor Salonu': '36.564500, 36.152073',
  'Denizciler Kapalı Spor Salonu': '36.564500, 36.152073'
};

const SportsAppointment = () => {
  const [regions, setRegions] = useState([]);
  const [expandedRegions, setExpandedRegions] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const today = new Date();
    today.setMinutes(today.getMinutes() - today.getTimezoneOffset());
    return today.toISOString().split('T')[0];
  });
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    idNumber: '',
    email: '',
    phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const formRef = useRef(null);

  const handleTimeSlotSelected = (slot) => {
    setSelectedTimeSlot(slot);
    setTimeout(() => {
      formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  // Payment modal states
  const [showPaymentSuccessModal, setShowPaymentSuccessModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: ''
  });

  const [paymentErrors, setPaymentErrors] = useState({});

  // Policy states
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsViewed, setTermsViewed] = useState(false);

  const [refundPolicyAccepted, setRefundPolicyAccepted] = useState(false);
  const [showRefundPolicyModal, setShowRefundPolicyModal] = useState(false);
  const [refundPolicyViewed, setRefundPolicyViewed] = useState(false);

  const [contractAccepted, setContractAccepted] = useState(false);
  const [showContractModal, setShowContractModal] = useState(false);
  const [contractViewed, setContractViewed] = useState(false);


  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [pendingAppointmentId, setPendingAppointmentId] = useState(null);
  const [verificationError, setVerificationError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);

  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [successMessage, setSuccessMessage] = useState(null);

  // Appointment Flow State
  const [viewStep, setViewStep] = useState('slots'); // 'slots' | 'details' | 'payment' | '3dsecure'
  const [paymentHtml, setPaymentHtml] = useState(null);

  useEffect(() => {
    fetchRegionsAndFacilities();
  }, []);

  useEffect(() => {
    if (selectedFacility) {
      fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
    }
  }, [selectedDate, selectedFacility?.id]);

  const fetchRegionsAndFacilities = async () => {
    try {
      setLoading(true);
      const response = await getSportsRegionsWithFacilities();
      if (response.isSuccess) {
        setRegions(response.data || []);
        setError(null);
      } else {
        setError(response.resultMessage || 'Veriler yüklenirken bir hata oluştu.');
      }
    } catch (err) {
      console.error('Bölgeler yüklenirken hata:', err);
      setError('Veriler yüklenirken bir hata oluştu. Lütfen tekrar deneyin.');
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableTimeSlots = async (facilityId, date) => {
    try {
      const response = await getSportsAvailableSlots(facilityId, date);

      if (response.isSuccess) {
        setSelectedFacility(prev => ({
          ...prev,
          timeSlots: response.data || []
        }));
      }
    } catch (err) {
      console.error('Saat dilimleri yüklenirken hata:', err);
    }
  };

  const handleCreateAppointment = async () => {
    if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.email || !formData.phone) {
      alert('Lütfen tüm zorunlu alanları doldurunuz.');
      return;
    }

    // Navigate to payment step inline
    setViewStep('payment');
  };

  const detectCardType = (cardNumber) => {
    const cleaned = cardNumber.replace(/\s/g, '');
    // Visa starts with 4
    if (cleaned.startsWith('4')) return '1';
    // MasterCard starts with 51-55 or 2221-2720
    if (/^5[1-5]/.test(cleaned) || /^2[2-7]/.test(cleaned)) return '2';
    // Default to unknown
    return null;
  };

  // Luhn Algorithm for Credit Card Validation
  const luhnCheck = (val) => {
    let checksum = 0;
    let j = 1;
    for (let i = val.length - 1; i >= 0; i--) {
      let calc = 0;
      calc = Number(val.charAt(i)) * j;
      if (calc > 9) {
        checksum = checksum + 1;
        calc = calc - 10;
      }
      checksum = checksum + calc;
      if (j == 1) {
        j = 2;
      } else {
        j = 1;
      }
    }
    return (checksum % 10) == 0;
  };

  const handlePayment = async () => {
    const errors = {};
    const cardNumber = paymentData.cardNumber.replace(/\s/g, '');
    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth() + 1;

    // Validate Card Number
    if (!cardNumber) {
      errors.cardNumber = 'Kart numarası gereklidir.';
    } else if (cardNumber.length !== 16) {
      errors.cardNumber = 'Kart numarası 16 haneli olmalıdır.';
    } else if (!luhnCheck(cardNumber)) {
      errors.cardNumber = 'Geçersiz kart numarası.';
    }

    // Validate Card Name
    if (!paymentData.cardName.trim()) {
      errors.cardName = 'Kart üzerindeki isim gereklidir.';
    }

    // Validate Expiry
    if (!paymentData.expiryMonth || !paymentData.expiryYear) {
      errors.expiry = 'Son kullanma tarihi seçiniz.';
    } else {
      const expYear = parseInt(paymentData.expiryYear);
      const expMonth = parseInt(paymentData.expiryMonth);
      if (expYear < currentYear || (expYear === currentYear && expMonth < currentMonth)) {
        errors.expiry = 'Kartın son kullanma tarihi geçmiştir.';
      }
    }

    // Validate CVV
    if (!paymentData.cvv) {
      errors.cvv = 'CVV gereklidir.';
    } else if (paymentData.cvv.length !== 3) {
      errors.cvv = 'CVV 3 haneli olmalıdır.';
    }

    if (Object.keys(errors).length > 0) {
      setPaymentErrors(errors);
      return; // Stop if validation fails
    }

    setPaymentErrors({}); // Clear errors if valid

    try {
      // Prepare payment initiation data
      const paymentRequestData = {
        facilityId: selectedFacility.id,
        appointmentDate: selectedDate,
        startTime: selectedTimeSlot.time.split(' - ')[0],
        endTime: selectedTimeSlot.time.split(' - ')[1],
        firstName: formData.firstName,
        lastName: formData.lastName,
        identityNumber: formData.idNumber,
        email: formData.email,
        phone: formData.phone,
        pan: paymentData.cardNumber.replace(/\s/g, ''),
        cv2: paymentData.cvv,
        expireMonth: paymentData.expiryMonth.padStart(2, '0'),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        expireYear: paymentData.expiryYear,
        cardType: detectCardType(paymentData.cardNumber),
        amount: selectedTimeSlot.price // Backend verifies this for security
      };

      const response = await initiatePayment(paymentRequestData);

      if (response.isSuccess && response.data?.html) {
        // Debug: Log the HTML response from backend
        console.log('=== BACKEND RESPONSE HTML ===');
        console.log(response.data.html);
        console.log('=============================');

        // Set embedded HTML content
        setPaymentHtml(response.data.html);
        setViewStep('3dsecure');
      } else {
        alert(response.resultMessage || 'Ödeme başlatılamadı. Lütfen tekrar deneyin.');
      }
    } catch (err) {
      console.error('Ödeme başlatılırken hata:', err);
      alert('Ödeme işlemi başlatılırken bir hata oluştu. Lütfen tekrar deneyin.');
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setVerificationError('Lütfen 6 haneli doğrulama kodunu giriniz.');
      return;
    }

    setIsVerifying(true);
    setVerificationError('');

    try {
      const response = await verifySportsAppointment(pendingAppointmentId, verificationCode);

      if (response.isSuccess) {
        setShowVerificationModal(false);
        setSuccessMessage({
          firstName: formData.firstName,
          lastName: formData.lastName,
          facilityName: selectedFacility.name,
          date: new Date(selectedDate).toLocaleDateString('tr-TR'),
          time: selectedTimeSlot.time
        });
        setShowSuccessModal(true);

        setFormData({ firstName: '', lastName: '', idNumber: '', email: '', phone: '' });
        setSelectedTimeSlot(null);
        setVerificationCode('');
        setPendingAppointmentId(null);
        fetchAvailableTimeSlots(selectedFacility.id, selectedDate);
      } else {
        setVerificationError(response.resultMessage || 'Doğrulama kodu hatalı.');
      }
    } catch (err) {
      console.error('Doğrulama hatası:', err);
      setVerificationError('Doğrulama kodu hatalı veya süresi dolmuş.');
    } finally {
      setIsVerifying(false);
    }
  };

  const toggleRegion = (regionId) => {
    setExpandedRegions(prev => {
      if (prev.includes(regionId)) {
        return prev.filter(id => id !== regionId);
      } else {
        return [...prev, regionId];
      }
    });
  };

  const handleFacilityClick = async (facility, region) => {
    setSelectedFacility({
      ...facility,
      regionName: region.name,
      timeSlots: []
    });
    setSelectedTimeSlot(null);

    await fetchAvailableTimeSlots(facility.id, selectedDate);
  };

  if (loading) {
    return (
      <div className="sports-appointment-page">
        <div className="container">
          <header className="sa-header">
            <h1>Spor Tesisleri Randevu Sistemi</h1>
          </header>
          <div className="sa-loading">
            <p>Yükleniyor...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sports-appointment-page">
        <div className="container">
          <header className="sa-header">
            <h1>Spor Tesisleri Randevu Sistemi</h1>
          </header>
          <div className="sa-error">
            <p>{error}</p>
            <button onClick={fetchRegionsAndFacilities} className="sa-retry-button">Tekrar Dene</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="sports-appointment-page">
      <div className="container">
        <header className="sa-header">
          <h1>Spor Tesisleri Randevu Sistemi</h1>
          <p>
            İskenderun Belediyesi spor tesislerini online randevu sistemi ile kiralayabilirsiniz.
            Tenis kortları, futbol sahaları, basketbol ve voleybol sahaları saatlik kiralama ile hizmetinizde.
          </p>
        </header>

        <div className="sa-content">
          <div className="sa-left-panel">
            <div className="sa-regions-list">
              {regions.map(region => (
                <div key={region.id} className="sa-region-item">
                  <button
                    type="button"
                    className={`sa-region-header ${expandedRegions.includes(region.id) ? 'expanded' : ''}`}
                    onClick={() => toggleRegion(region.id)}
                  >
                    <span className="sa-region-icon">
                      {expandedRegions.includes(region.id) ? <FaChevronDown /> : <FaChevronRight />}
                    </span>
                    <span className="sa-region-name">{region.name}</span>

                  </button>

                  {expandedRegions.includes(region.id) && (
                    <div className="sa-facilities-list">
                      {region.facilities.map(facility => {
                        const Icon = facilityIcons[facility.type] || FaFutbol;
                        const isSelected = selectedFacility?.id === facility.id;

                        return (
                          <button
                            key={facility.id}
                            type="button"
                            className={`sa-facility-card ${isSelected ? 'selected' : ''}`}
                            onClick={() => handleFacilityClick(facility, region)}
                          >
                            <div className="sa-facility-icon">
                              <Icon />
                            </div>
                            <div className="sa-facility-info">
                              <div className="sa-facility-name">{facility.name}</div>
                              <div className="sa-facility-capacity">{facility.capacity} Kişi</div>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="sa-right-panel">
            {selectedFacility ? (
              <div className="sa-facility-details">


                <div className="sa-appointment-section">
                  {/* PROGRESS STEPPER */}
                  <div className="sa-stepper">
                    {['slots', 'details', 'confirmation', 'payment', '3dsecure'].map((step, index) => {
                      const stepNames = { slots: 'Zaman', details: 'Bilgiler', confirmation: 'Onay', payment: 'Ödeme', '3dsecure': 'Tamamla' };
                      const currentStepIndex = ['slots', 'details', 'confirmation', 'payment', '3dsecure'].indexOf(viewStep);
                      const isCompleted = index < currentStepIndex;
                      const isActive = index === currentStepIndex;

                      return (
                        <div
                          key={step}
                          className={`sa-step ${isActive ? 'active' : ''} ${isCompleted ? 'completed' : ''} ${index <= currentStepIndex ? 'clickable' : ''}`}
                          onClick={() => {
                            // Allow navigation to previous steps or current step
                            if (index <= currentStepIndex) {
                              setViewStep(step);
                            }
                          }}
                        >
                          <div className="sa-step-circle">
                            {isCompleted ? <FaCheckCircle /> : index + 1}
                          </div>
                          <span className="sa-step-label">{stepNames[step]}</span>
                          {index < 4 && <div className="sa-step-line" />}
                        </div>
                      );
                    })}
                  </div>

                  {/* STEP 1: SLOTS VIEW */}
                  {viewStep === 'slots' && (
                    <>
                      <div className="sa-date-picker-section">
                        <label htmlFor="appointment-date">Tarih Seçin</label>
                        <input
                          id="appointment-date"
                          type="date"
                          value={selectedDate}
                          onChange={(e) => setSelectedDate(e.target.value)}
                          min={new Date().toISOString().split('T')[0]}
                          className="sa-date-input"
                        />
                      </div>

                      <div className="sa-time-slots-section">
                        <h4>Müsait Saatler <span className="sa-working-hours">(08:00 - 23:59)</span></h4>
                        <div className="sa-time-slots-grid">
                          {selectedFacility.timeSlots.map((slot, index) => (
                            <button
                              key={index}
                              type="button"
                              className={`sa-time-slot ${slot.available ? 'available' : 'occupied'} ${selectedTimeSlot?.time === slot.time ? 'selected' : ''}`}
                              disabled={!slot.available}
                              onClick={() => slot.available && handleTimeSlotSelected(slot)}
                            >
                              <span className="sa-slot-time">{slot.time}</span>
                              <span className="sa-slot-price" style={{ display: 'block', fontSize: '0.8rem', marginTop: '2px', fontWeight: 'bold', color: slot.available ? '#237CB2' : '#999' }}>
                                {slot.price} TL
                              </span>
                              <span className="sa-slot-status">
                                {slot.available ? 'Müsait' : 'Dolu'}
                              </span>
                            </button>
                          ))}
                        </div>

                        {selectedTimeSlot && (
                          <div className="sa-action-row">
                            <button
                              className="sa-submit-button sa-btn-next"
                              onClick={() => setViewStep('details')}
                            >
                              Randevu Al <FaChevronRight />
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}

                  {/* STEP 2: DETAILS VIEW */}
                  {/* STEP 2: DETAILS VIEW */}
                  {viewStep === 'details' && selectedTimeSlot && (
                    <div className="sa-booking-form" ref={formRef}>
                      <div className="sa-form-header">
                        <h4>Randevu Bilgileri</h4>
                        <button
                          className="sa-icon-back-button"
                          onClick={() => setViewStep('slots')}
                          title="Geri Dön"
                        >
                          <FaArrowLeft />
                        </button>
                      </div>

                      <div className="sa-form-grid">
                        <div className="sa-form-group">
                          <label htmlFor="firstName">Ad <span className="sa-required">*</span></label>
                          <input id="firstName" type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })} placeholder="Adınızı giriniz" className="sa-form-input" />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="lastName">Soyad <span className="sa-required">*</span></label>
                          <input id="lastName" type="text" value={formData.lastName} onChange={(e) => setFormData({ ...formData, lastName: e.target.value })} placeholder="Soyadınızı giriniz" className="sa-form-input" />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="idNumber">T.C. Kimlik No <span className="sa-required">*</span></label>
                          <input id="idNumber" type="text" value={formData.idNumber} onChange={(e) => setFormData({ ...formData, idNumber: e.target.value.replace(/\D/g, '').slice(0, 11) })} placeholder="TC Kimlik No giriniz" className="sa-form-input" maxLength="11" />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="email">E-Posta <span className="sa-required">*</span></label>
                          <input id="email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="E-posta adresiniz" className="sa-form-input" />
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="phone">Telefon <span className="sa-required">*</span></label>
                          <input id="phone" type="tel" value={formData.phone} onChange={(e) => { const value = e.target.value.replace(/\D/g, ''); if (value.length <= 10) setFormData({ ...formData, phone: value }); }} placeholder="5XXXXXXXXX" className="sa-form-input" maxLength="10" />
                        </div>

                        <div className="sa-form-group sa-appointment-summary">
                          <label>Randevu Saati</label>
                          <div className="sa-summary-box">
                            <strong>{selectedTimeSlot.time}</strong>
                            <span>{new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                          </div>
                        </div>
                      </div>

                      <div className="sa-terms-checkbox">
                        <input type="checkbox" id="termsAccept" checked={termsAccepted} onChange={(e) => { if (!termsViewed) { e.preventDefault(); setShowTermsModal(true); } else { setTermsAccepted(e.target.checked); } }} onClick={(e) => { if (!termsViewed) { e.preventDefault(); setShowTermsModal(true); } }} />
                        <label htmlFor="termsAccept"><span className="sa-terms-link" onClick={(e) => { e.preventDefault(); setShowTermsModal(true); }}>Hizmet Şartları</span>'nı okudum ve kabul ediyorum</label>
                      </div>
                      <div className="sa-terms-checkbox">
                        <input type="checkbox" id="refundPolicyAccept" checked={refundPolicyAccepted} onChange={(e) => { if (!refundPolicyViewed) { e.preventDefault(); setShowRefundPolicyModal(true); } else { setRefundPolicyAccepted(e.target.checked); } }} onClick={(e) => { if (!refundPolicyViewed) { e.preventDefault(); setShowRefundPolicyModal(true); } }} />
                        <label htmlFor="refundPolicyAccept"><span className="sa-terms-link" onClick={(e) => { e.preventDefault(); setShowRefundPolicyModal(true); }}>Para İade Politikası</span>'nı okudum ve kabul ediyorum</label>
                      </div>
                      <div className="sa-terms-checkbox">
                        <input type="checkbox" id="contractAccept" checked={contractAccepted} onChange={(e) => { if (!contractViewed) { e.preventDefault(); setShowContractModal(true); } else { setContractAccepted(e.target.checked); } }} onClick={(e) => { if (!contractViewed) { e.preventDefault(); setShowContractModal(true); } }} />
                        <label htmlFor="contractAccept"><span className="sa-terms-link" onClick={(e) => { e.preventDefault(); setShowContractModal(true); }}>Mesafeli Satış Sözleşmesi</span>'ni okudum ve kabul ediyorum</label>
                      </div>

                      <div className="sa-action-row">
                        <button
                          className="sa-submit-button sa-btn-next"
                          onClick={() => {
                            if (!formData.firstName || !formData.lastName || !formData.idNumber || !formData.email || !formData.phone) {
                              alert('Lütfen tüm zorunlu alanları doldurunuz.');
                              return;
                            }
                            setViewStep('confirmation');
                          }}
                          disabled={!termsAccepted || !refundPolicyAccepted || !contractAccepted}
                        >
                          Devam Et <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* STEP 3: CONFIRMATION VIEW */}
                  {viewStep === 'confirmation' && selectedTimeSlot && (
                    <div className="sa-booking-form">
                      <div className="sa-form-header">
                        <h4>Bilgileri Onayla</h4>
                        <button
                          className="sa-icon-back-button"
                          onClick={() => setViewStep('details')}
                          title="Geri Dön"
                        >
                          <FaArrowLeft />
                        </button>
                      </div>

                      <div className="sa-confirmation-details">
                        <div className="sa-confirmation-row">
                          <span className="sa-label">Tesis:</span>
                          <span className="sa-value">{selectedFacility.name} ({selectedFacility.regionName})</span>
                        </div>
                        <div className="sa-confirmation-row">
                          <span className="sa-label">Tarih:</span>
                          <span className="sa-value">{new Date(selectedDate).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
                        </div>
                        <div className="sa-confirmation-row">
                          <span className="sa-label">Saat:</span>
                          <span className="sa-value">{selectedTimeSlot.time}</span>
                        </div>
                        <div className="sa-divider" />
                        <div className="sa-confirmation-row">
                          <span className="sa-label">Ad Soyad:</span>
                          <span className="sa-value">{formData.firstName} {formData.lastName}</span>
                        </div>
                        <div className="sa-confirmation-row">
                          <span className="sa-label">TC Kimlik:</span>
                          <span className="sa-value">{formData.idNumber}</span>
                        </div>
                        <div className="sa-confirmation-row">
                          <span className="sa-label">Email:</span>
                          <span className="sa-value">{formData.email}</span>
                        </div>
                        <div className="sa-confirmation-row">
                          <span className="sa-label">Telefon:</span>
                          <span className="sa-value">{formData.phone}</span>
                        </div>
                        <div className="sa-divider" />
                        <div className="sa-confirmation-total">
                          <span className="sa-label">Toplam Tutar:</span>
                          <span className="sa-value highlight">{selectedTimeSlot.price} TL</span>
                        </div>
                      </div>

                      <div className="sa-action-row">
                        <button className="sa-submit-button sa-btn-next" onClick={() => setViewStep('payment')}>
                          Ödemeye Geç <FaChevronRight />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: PAYMENT VIEW */}
                  {viewStep === 'payment' && (
                    <div className="sa-booking-form">
                      <div className="sa-form-header">
                        <h4>Kart Bilgileri</h4>
                        <button
                          className="sa-icon-back-button"
                          onClick={() => setViewStep('confirmation')}
                          title="Geri Dön"
                        >
                          <FaArrowLeft />
                        </button>
                        <div className="sa-payment-total">
                          Toplam: {selectedTimeSlot.price} TL
                        </div>
                      </div>

                      <div className="sa-card-info">
                        <div className="sa-form-group">
                          <label htmlFor="cardNumber">Kart Numarası</label>
                          <div className="sa-input-with-icon">
                            <input
                              id="cardNumber"
                              type="text"
                              value={paymentData.cardNumber}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                                if (value.length <= 16) {
                                  setPaymentData({ ...paymentData, cardNumber: formatted });
                                  if (paymentErrors.cardNumber) setPaymentErrors({ ...paymentErrors, cardNumber: '' });
                                }
                              }}
                              placeholder="0000 0000 0000 0000"
                              className={`sa-form-input ${paymentErrors.cardNumber ? 'error' : ''}`}
                              maxLength="19"
                            />
                            <div className="sa-card-brand-icon">
                              {detectCardType(paymentData.cardNumber) === '1' && <FaCcVisa size={28} color="#1a1f71" />}
                              {detectCardType(paymentData.cardNumber) === '2' && (
                                <svg width="32" height="20" viewBox="0 0 32 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                                  <circle cx="10" cy="10" r="10" fill="#EB001B" />
                                  <circle cx="22" cy="10" r="10" fill="#F79E1B" />
                                  <path d="M16 2 A10 10 0 0 0 16 18 A10 10 0 0 0 16 2 Z" fill="#FF5F00" />
                                </svg>
                              )}
                              {!detectCardType(paymentData.cardNumber) && <FaCreditCard size={28} color="#cbd5e1" />}
                            </div>
                          </div>
                          {paymentErrors.cardNumber && <div className="sa-error-message">{paymentErrors.cardNumber}</div>}
                        </div>

                        <div className="sa-form-group">
                          <label htmlFor="cardName">Kart Üzerindeki İsim</label>
                          <input
                            id="cardName"
                            type="text"
                            value={paymentData.cardName}
                            onChange={(e) => {
                              setPaymentData({ ...paymentData, cardName: e.target.value.toUpperCase() });
                              if (paymentErrors.cardName) setPaymentErrors({ ...paymentErrors, cardName: '' });
                            }}
                            placeholder="AD SOYAD"
                            className={`sa-form-input ${paymentErrors.cardName ? 'error' : ''}`}
                          />
                          {paymentErrors.cardName && <div className="sa-error-message">{paymentErrors.cardName}</div>}
                        </div>

                        <div className="sa-form-row">
                          <div className="sa-form-group">
                            <label>Son Kullanma Tarihi</label>
                            <div className="sa-expiry-selects">
                              <select
                                id="expiryMonth"
                                value={paymentData.expiryMonth}
                                onChange={(e) => {
                                  setPaymentData({ ...paymentData, expiryMonth: e.target.value });
                                  if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                }}
                                className={`sa-form-select ${paymentErrors.expiry ? 'error' : ''}`}
                              >
                                <option value="">Ay</option>
                                {Array.from({ length: 12 }, (_, i) => {
                                  const month = String(i + 1).padStart(2, '0');
                                  return <option key={month} value={month}>{month}</option>;
                                })}
                              </select>
                              <select
                                id="expiryYear"
                                value={paymentData.expiryYear}
                                onChange={(e) => {
                                  setPaymentData({ ...paymentData, expiryYear: e.target.value });
                                  if (paymentErrors.expiry) setPaymentErrors({ ...paymentErrors, expiry: '' });
                                }}
                                className={`sa-form-select ${paymentErrors.expiry ? 'error' : ''}`}
                              >
                                <option value="">Yıl</option>
                                {Array.from({ length: 45 }, (_, i) => {
                                  const year = 2026 + i;
                                  return <option key={year} value={year}>{year}</option>;
                                })}
                              </select>
                            </div>
                            {paymentErrors.expiry && <div className="sa-error-message">{paymentErrors.expiry}</div>}
                          </div>

                          <div className="sa-form-group">
                            <label htmlFor="cvv">CVV</label>
                            <input
                              id="cvv"
                              type="text"
                              value={paymentData.cvv}
                              onChange={(e) => {
                                const value = e.target.value.replace(/\D/g, '');
                                if (value.length <= 3) {
                                  setPaymentData({ ...paymentData, cvv: value });
                                  if (paymentErrors.cvv) setPaymentErrors({ ...paymentErrors, cvv: '' });
                                }
                              }}
                              placeholder="123"
                              className={`sa-form-input ${paymentErrors.cvv ? 'error' : ''}`}
                              maxLength="3"
                            />
                            {paymentErrors.cvv && <div className="sa-error-message">{paymentErrors.cvv}</div>}
                          </div>
                        </div>
                      </div>

                      <div className="sa-action-row">
                        <button type="button" className="sa-submit-button sa-btn-next" onClick={handlePayment}>Ödemeyi Tamamla <FaCheckCircle style={{ marginLeft: 8 }} /></button>
                      </div>
                    </div>
                  )}

                  {/* STEP 4: 3D SECURE VIEW */}
                  {viewStep === '3dsecure' && paymentHtml && (
                    <div className="sa-3dsecure-iframe" style={{ width: '100%', height: '600px', border: '1px solid #e2e8f0', borderRadius: '12px' }}>
                      <iframe
                        title="3D Secure Payment"
                        srcDoc={paymentHtml}
                        style={{ width: '100%', height: '100%', border: 'none' }}
                      />
                    </div>
                  )}


                </div>
              </div>
            ) : (
              <div className="sa-no-selection">
                <FaFutbol className="sa-no-selection-icon" />
                <h3>Saha Seçin</h3>
                <p>Randevu almak için sol panelden bölge ve saha seçiniz</p>
              </div>
            )}
          </div>
        </div>

        {showVerificationModal && (
          <div className="sa-verification-modal-overlay" onClick={() => !isVerifying && setShowVerificationModal(false)}>
            <div className="sa-verification-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3>Email Doğrulama</h3>
                <button
                  className="sa-modal-close"
                  onClick={() => setShowVerificationModal(false)}
                  disabled={isVerifying}
                >
                  ×
                </button>
              </div>
              <div className="sa-modal-body">
                <p className="sa-verification-instruction">
                  E-posta adresinize gönderilen 6 haneli doğrulama kodunu giriniz:
                </p>
                <input
                  type="text"
                  className="sa-verification-input"
                  maxLength="6"
                  value={verificationCode}
                  onChange={(e) => {
                    const value = e.target.value.replace(/\D/g, '');
                    setVerificationCode(value);
                    if (verificationError) setVerificationError('');
                  }}
                  placeholder="000000"
                  disabled={isVerifying}
                  autoFocus
                />
                {verificationError && (
                  <div className="sa-verification-error">{verificationError}</div>
                )}
                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={handleVerifyCode}
                    disabled={isVerifying || verificationCode.length !== 6}
                  >
                    {isVerifying ? 'Doğrulanıyor...' : 'Onayla'}
                  </button>
                  <button
                    className="sa-btn-cancel"
                    onClick={() => setShowVerificationModal(false)}
                    disabled={isVerifying}
                  >
                    İptal
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {showSuccessModal && successMessage && (
          <div className="sa-verification-modal-overlay" onClick={() => setShowSuccessModal(false)}>
            <div className="sa-verification-modal sa-success-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-body">
                <div className="sa-success-icon">
                  <FaCheckCircle />
                </div>
                <h3>Randevunuz Onaylandı!</h3>
                <p>Bilgileriniz mail üzerinden gönderilmiştir.</p>

                <div className="sa-success-details">
                  <div className="sa-detail-row">
                    <span className="label">Ad Soyad:</span>
                    <span className="value">{successMessage.firstName} {successMessage.lastName}</span>
                  </div>
                  <div className="sa-detail-row">
                    <span className="label">Saha:</span>
                    <span className="value">{successMessage.facilityName}</span>
                  </div>
                  <div className="sa-detail-row">
                    <span className="label">Tarih:</span>
                    <span className="value">{successMessage.date}</span>
                  </div>
                  <div className="sa-detail-row">
                    <span className="label">Saat:</span>
                    <span className="value">{successMessage.time}</span>
                  </div>
                </div>

                <div className="sa-verification-actions">
                  <button
                    className="sa-btn-verify"
                    onClick={() => setShowSuccessModal(false)}
                  >
                    Tamam
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}


        {showTermsModal && (
          <div className="sa-verification-modal-overlay" onClick={() => {
            setShowTermsModal(false);
            setTermsViewed(true);
          }}>
            <div className="sa-verification-modal sa-terms-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3>Hizmet Şartları</h3>
                <button
                  className="sa-modal-close"
                  onClick={() => {
                    setShowTermsModal(false);
                    setTermsViewed(true);
                  }}
                >
                  ×
                </button>
              </div>
              <TermsOfService />
              <div className="sa-verification-actions">
                <button
                  className="sa-btn-verify"
                  onClick={() => {
                    setTermsAccepted(true);
                    setTermsViewed(true);
                    setShowTermsModal(false);
                  }}
                >
                  Onaylıyorum
                </button>
              </div>
            </div>
          </div>
        )}

        {showRefundPolicyModal && (
          <div className="sa-verification-modal-overlay" onClick={() => {
            setShowRefundPolicyModal(false);
            setRefundPolicyViewed(true);
          }}>
            <div className="sa-verification-modal sa-terms-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3>Para İade Politikası</h3>
                <button
                  className="sa-modal-close"
                  onClick={() => {
                    setShowRefundPolicyModal(false);
                    setRefundPolicyViewed(true);
                  }}
                >
                  ×
                </button>
              </div>
              <RefundPolicy />
              <div className="sa-verification-actions">
                <button
                  className="sa-btn-verify"
                  onClick={() => {
                    setRefundPolicyAccepted(true);
                    setRefundPolicyViewed(true);
                    setShowRefundPolicyModal(false);
                  }}
                >
                  Onaylıyorum
                </button>
              </div>
            </div>
          </div>
        )}

        {showContractModal && (
          <div className="sa-verification-modal-overlay" onClick={() => {
            setShowContractModal(false);
            setContractViewed(true);
          }}>
            <div className="sa-verification-modal sa-terms-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sa-modal-header">
                <h3>Mesafeli Satış Sözleşmesi</h3>
                <button
                  className="sa-modal-close"
                  onClick={() => {
                    setShowContractModal(false);
                    setContractViewed(true);
                  }}
                >
                  ×
                </button>
              </div>
              <DistanceSalesContract />
              <div className="sa-verification-actions">
                <button
                  className="sa-btn-verify"
                  onClick={() => {
                    setContractAccepted(true);
                    setContractViewed(true);
                    setShowContractModal(false);
                  }}
                >
                  Onaylıyorum
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SportsAppointment;
